import { useCallback, useEffect, useState } from 'react';
import { Box, Container, Stack } from '@mui/material';
import CapacityBar from '../components/CapacityBar.jsx';
import HeroBanner from '../components/HeroBanner.jsx';
import ReservationForm from '../components/ReservationForm.jsx';
import {
  extractApiError,
  fetchCapacity,
  forgetReservation,
  getRememberedReservation,
  lookupPreregistration,
} from '../api.js';

export default function Home() {
  const [capacity, setCapacity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Préréservation reconnue depuis le localStorage (saisie précédente sur ce navigateur).
  const [remembered, setRemembered] = useState(() => getRememberedReservation());
  // Statut réel **du client** (récupéré côté API à partir de l'email mémorisé).
  // null = pas encore chargé ou pas de mémorisation ; { ... } = données fraîches.
  const [clientStatus, setClientStatus] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCapacity();
      setCapacity(data);
    } catch (err) {
      setError(extractApiError(err, 'Impossible de charger la capacité.'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Récupère le statut réel de la préréservation du client à partir de son email.
  // Appelée à chaque refresh de capacité pour rester à jour (le club a pu confirmer
  // ou annuler la résa depuis la dernière visite).
  const refreshClientStatus = useCallback(async (email) => {
    if (!email) {
      setClientStatus(null);
      return;
    }
    try {
      const data = await lookupPreregistration(email);
      setClientStatus(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        // La résa a été annulée ou n'existe plus côté serveur : on garde le
        // bandeau (avec son email) mais on supprime les chips de statut.
        setClientStatus(null);
      }
      // Autres erreurs (réseau) : on ne touche pas au statut affiché.
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [refresh]);

  // Charge / rafraîchit le statut client dès qu'on a (ou change) un email mémorisé.
  useEffect(() => {
    refreshClientStatus(remembered?.email);
  }, [remembered?.email, refreshClientStatus]);

  // Callback `onCreated` du formulaire : on rafraîchit la capacité ET on met
  // à jour la préréservation mémorisée affichée dans le CapacityBar.
  const handleCreated = useCallback((created) => {
    setRemembered({
      id: created.id,
      email: created.email,
      first_name: created.first_name,
      last_name: created.last_name,
      meals: created.meals,
    });
    refresh();
    refreshClientStatus(created.email);
  }, [refresh, refreshClientStatus]);

  const handleForget = useCallback(() => {
    forgetReservation();
    setRemembered(null);
    setClientStatus(null);
  }, []);

  return (
    <Container maxWidth="lg">
      <Stack spacing={3}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '3fr 1fr' },
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          <HeroBanner
            overline="50 ans du club · Quilles de huit"
            title="Soirée Moules-Frites"
            subtitle="Sport Quilles Séverac d'Aveyron · 17 octobre 2026"
          />
          <CapacityBar
            capacity={capacity}
            loading={loading}
            error={error}
            remembered={remembered}
            clientStatus={clientStatus}
            onForget={handleForget}
          />
        </Box>
        <ReservationForm onCreated={handleCreated} capacity={capacity} />
      </Stack>
    </Container>
  );
}
